import { useEffect, useState } from "react";
import Header from "@/components/Header";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UsersThree, Plus, Key, Trash, ShieldCheck, User as UserIcon, LockKeyOpen } from "@phosphor-icons/react";

const emptyCreate = { name: "", email: "", role: "user" };

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createUser = async () => {
    if (!createForm.email) {
      toast.error("Email is required");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/users", createForm);
      setUsers((prev) => [...prev, { ...data, is_self: false }]);
      toast.success(
        data.invite_sent
          ? `Invite sent to ${data.email}`
          : `User created, but the invite email couldn't be sent. Use "Send reset link".`
      );
      setCreateOpen(false);
      setCreateForm(emptyCreate);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role: data.role } : x)));
      toast.success(`${u.email} is now ${data.role}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update role");
    }
  };

  const sendReset = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/send-reset`);
      toast.success(`Password reset link emailed to ${u.email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to send reset link");
    }
  };

  const deleteUser = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success(`${u.email} removed`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to delete user");
    }
  };

  const unlockUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/unlock`);
      toast.success(`${u.email} unlocked — they can sign in again`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to unlock user");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <UsersThree size={26} className="text-primary" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">User management</h2>
          </div>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="admin-add-user-btn" className="gap-1.5">
                <Plus size={16} /> Add user
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="admin-create-dialog">
              <DialogHeader>
                <DialogTitle>Create a new user</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label htmlFor="new-name">Name</Label>
                  <Input
                    id="new-name"
                    data-testid="admin-new-name"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Jane Doe"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new-email">Email</Label>
                  <Input
                    id="new-email"
                    data-testid="admin-new-email"
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="jane@fmiqs.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select
                    value={createForm.role}
                    onValueChange={(v) => setCreateForm({ ...createForm, role: v })}
                  >
                    <SelectTrigger data-testid="admin-new-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">
                  The user will receive an email invitation to set their own password. No password needed here.
                </p>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button data-testid="admin-create-submit" onClick={createUser} disabled={creating}>
                  {creating ? "Sending invite…" : "Send invite"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="font-mono text-sm text-muted-foreground animate-pulse">Loading…</div>
        ) : (
          <div className="border border-border bg-white" data-testid="admin-user-list">
            {users.map((u) => (
              <div
                key={u.id}
                data-testid={`admin-user-${u.id}`}
                className="flex items-center justify-between px-4 py-3 border-b border-border last:border-b-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                    {u.role === "admin" ? (
                      <ShieldCheck size={18} className="text-primary" />
                    ) : (
                      <UserIcon size={18} className="text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate flex items-center gap-2">
                      {u.name || u.email.split("@")[0]}
                      {u.is_self && <Badge variant="secondary" className="text-[10px]">You</Badge>}
                    </div>
                    <div className="font-mono text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Select
                    value={u.role}
                    onValueChange={(v) => changeRole(u, v)}
                    disabled={u.is_self}
                  >
                    <SelectTrigger data-testid={`admin-role-${u.id}`} className="w-[110px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`admin-unlock-${u.id}`}
                    onClick={() => unlockUser(u)}
                    className="gap-1.5 h-8"
                    title="Clear 'too many attempts' lockout"
                  >
                    <LockKeyOpen size={14} /> Unlock
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`admin-send-reset-${u.id}`}
                    onClick={() => sendReset(u)}
                    className="gap-1.5 h-8"
                    title="Email a password reset link to this user"
                  >
                    <Key size={14} /> Reset link
                  </Button>

                  {!u.is_self && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`admin-delete-${u.id}`}
                          className="text-muted-foreground hover:text-destructive transition-colors p-2"
                        >
                          <Trash size={16} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {u.email}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently deletes the user account. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            data-testid={`admin-delete-confirm-${u.id}`}
                            onClick={() => deleteUser(u)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
